import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { t } from 'copy/t';

import { StyledForm, Row } from './styledComponents';
import { sendEmailConfirmation } from '../actions'
import InputField  from "components/InputField"
import { Controller, useForm } from 'react-hook-form'
import * as yup from "yup";

const schema = yup
  .object({
    email: yup.string().email().required(),
    password: yup.string().required(),
  })
  .required();

export const AccountForm = ({
  initialValues,
  handleSubmit,
  resendEmailConfirmation,
  openInvalidPasswordModal,
  openInvalidEmailModal,
  submitForm,
  currentPasswordError,
  savedCount = 0,
}) => {
  const { control, setValue, watch } = useForm();
  const [currentPassword, setCurrentPassword] = useState('');
  const [requiredError, setRequiredError] = useState(false);

  // Rails asks for the current password only when the email or password changes.
  const email = watch('email');
  const newPassword = watch('password');
  const emailChanged = email !== undefined && email !== initialValues.email;
  const needsCurrentPassword = emailChanged || Boolean(newPassword);

  // Clear the pending password edit once a save goes through.
  const lastSaved = useRef(savedCount);
  const passwordSubmitted = useRef(false);
  useEffect(() => {
    if (savedCount !== lastSaved.current) {
      lastSaved.current = savedCount;
      // Only a save that carried the new password clears it; an unrelated
      // name/company save must not erase a half-typed password.
      if (passwordSubmitted.current) {
        passwordSubmitted.current = false;
        setValue('password', '');
        setCurrentPassword('');
      }
    }
  }, [savedCount]);

  const errorMessage = requiredError
    ? t('toast.account.currentPasswordRequired')
    : currentPasswordError;

  useEffect(() => {
    if (initialValues) {
      Object.keys(initialValues).map((name) => {
        setValue(name, initialValues[name]);
      });
    }
  }, []);

  const handleSubmitValidation = (value) => {
    const sensitive = 'email' in value || 'password' in value;
    if (sensitive && !currentPassword) {
      setRequiredError(true);
      return;
    }
    const payload = sensitive ? { ...value, current_password: currentPassword } : value;
    // Validate every pending field first, then submit once.
    Promise.all(
      Object.keys(value).map((name) =>
        schema.validateAt(name, value).catch((error) => {
          if (name === 'email') {
            openInvalidEmailModal({ ...error });
          } else {
            openInvalidPasswordModal({ ...error });
          }
          throw error;
        })
      )
    ).then(() => {
      passwordSubmitted.current = 'password' in value;
      handleSubmit(payload);
    }).catch(() => {});
  }

  // A save without the password (name, company) supersedes any earlier
  // password save, so a rejected one cannot be cleared by this unrelated success.
  const handlePlainSubmit = (data) => {
    passwordSubmitted.current = false;
    return handleSubmit(data);
  };

  return (
    <StyledForm enableReinitialize={true}>

      {[
        ['First Name'.toUpperCase(), 'first_name',{}],
        ['Last Name'.toUpperCase(), 'last_name',{}],
        ['Company'.toUpperCase(), 'company',{}],
        [
          'Login Email (' + (initialValues.unconfirmed_email ? 'unconfirmed' : 'confirmed') + ')',
          initialValues.unconfirmed_email ? 'unconfirmed_email' : 'email',
          {
            customActionBtn: initialValues.unconfirmed_email ? resendEmailConfirmation : false,
            updateBtnText: 'Update Email',
            editBtnText: initialValues.unconfirmed_email ? "RESEND" : "change email".toUpperCase(),
            inPlace: true,
            inPlaceOnSubmit:  (data) => handleSubmitValidation(data)
          }
        ],
        [
          'Password'.toUpperCase(),
          'password',
          {
            type: 'password',
            inPlaceOnSubmit: (data) => handleSubmitValidation(data)
          }

        ]
      ].map((item) =>  (
          <Row key={item[1]}>
            <Controller
              control={control}
              name={item[1]}
              render={({ field: { ref, ...fieldRest } }) => (
                <InputField 
                  label={item[0]}
                  inPlace={true}
                  inPlaceOnSubmit={handlePlainSubmit}
                  {...item[2]}
                  field={fieldRest}
                  {...fieldRest}
                />
              )}
            />
          </Row>
      ))}

      {needsCurrentPassword && (
        <Row>
          <label
            htmlFor="account-current-password"
            className="block text-xs font-semibold tracking-wide mb-1 uppercase"
          >
            Current password
          </label>
          <input
            id="account-current-password"
            type="password"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-red-400"
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              setRequiredError(false);
            }}
            autoComplete="current-password"
            aria-invalid={Boolean(errorMessage)}
            aria-describedby={errorMessage ? 'account-current-password-error' : undefined}
          />
          {errorMessage && (
            <p id="account-current-password-error" role="alert" className="mt-2 text-xs text-red-600">
              {errorMessage}
            </p>
          )}
          <button
            type="button"
            className="mt-3 px-6 py-2 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700 transition-colors"
            onClick={() => {
              const pending = {};
              if (emailChanged) pending.email = email;
              if (newPassword) pending.password = newPassword;
              handleSubmitValidation(pending);
            }}
          >
            Confirm change
          </button>
          <p className="mt-2 text-xs text-gray-500">
            <Link to="/forgot-password" className="underline">Forgot your password?</Link>
          </p>
        </Row>
      )}
    </StyledForm>
  );
}

const mapStateToProps = (state) => ({
  currentPasswordError: state.account?.currentPasswordError ?? null,
  savedCount: state.account?.savedCount ?? 0,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  resendEmailConfirmation: () => {
    dispatch(sendEmailConfirmation);
  },
  openInvalidPasswordModal: (modalProps) =>
    toast.error(t('toast.account.passwordRequirements'), { description: t('toast.account.passwordRequirementsDescription') }),

  openInvalidEmailModal: () => toast.error(t('toast.invalidEmail')),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AccountForm);
